import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useState } from 'react';

import { Badge, Spinner } from '@terra/ui';

import { api } from '../lib/api';

export function AgentDetailsDrawer({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<
    'performance' | 'leads' | 'orders' | 'payouts' | 'audit'
  >('performance');

  const performance = useQuery({
    queryKey: ['agent', agentId, 'perf'],
    queryFn: () => api.agents.getPerformance(agentId),
  });
  const leads = useQuery({
    queryKey: ['agent', agentId, 'leads'],
    queryFn: () => api.agents.getLeads(agentId),
  });
  const orders = useQuery({
    queryKey: ['agent', agentId, 'orders'],
    queryFn: () => api.agents.getOrders(agentId),
  });
  const payouts = useQuery({
    queryKey: ['agent', agentId, 'payouts'],
    queryFn: () => api.agents.getPayouts(agentId),
  });
  const audit = useQuery({
    queryKey: ['agent', agentId, 'audit'],
    queryFn: () => api.agents.getAuditLogs(agentId),
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="bg-ink/40 absolute inset-0 backdrop-blur-sm"
      />
      <motion.div
        initial={{ x: '100%', opacity: 0.5 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0.5 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="bg-surface relative flex w-full max-w-2xl flex-col shadow-2xl"
      >
        <div className="border-border/60 flex items-center justify-between border-b p-6">
          <h2 className="text-ink text-lg font-black">Agent Details</h2>
          <button onClick={onClose} className="text-ink-muted hover:text-ink transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-border/60 border-b">
          <div className="flex gap-1 p-2">
            {[
              { id: 'performance', label: 'Performance' },
              { id: 'leads', label: 'Assigned Leads' },
              { id: 'orders', label: 'Assigned Orders' },
              { id: 'payouts', label: 'Payouts' },
              { id: 'audit', label: 'Audit Trail' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`rounded-lg px-4 py-2 text-[13px] font-bold transition-all ${
                  activeTab === tab.id
                    ? 'bg-ink text-white shadow-sm'
                    : 'text-ink-muted hover:bg-surface-subtle hover:text-ink'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'performance' &&
            (performance.isLoading ? (
              <Spinner />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="border-border/60 rounded-xl border p-4 shadow-sm">
                  <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                    Total Leads
                  </p>
                  <p className="text-ink mt-1 text-2xl font-black">
                    {performance.data?.totalLeads}
                  </p>
                </div>
                <div className="border-border/60 rounded-xl border p-4 shadow-sm">
                  <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                    Total Orders
                  </p>
                  <p className="text-ink mt-1 text-2xl font-black">
                    {performance.data?.totalOrders}
                  </p>
                </div>
                <div className="border-border/60 rounded-xl border p-4 shadow-sm">
                  <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                    Lifetime Commission
                  </p>
                  <p className="text-ink mt-1 text-2xl font-black">
                    ₹{((performance.data?.totalEarningsPaise || 0) / 100).toFixed(2)}
                  </p>
                  <p className="text-ink-muted mt-1 text-[11px] font-medium">
                    Unpaid ₹{((performance.data?.pendingPaise || 0) / 100).toFixed(2)} · In payout ₹
                    {((performance.data?.inPayoutPaise || 0) / 100).toFixed(2)} · Paid ₹
                    {((performance.data?.paidPaise || 0) / 100).toFixed(2)}
                  </p>
                </div>
                <div className="border-border/60 rounded-xl border p-4 shadow-sm">
                  <p className="text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                    Conversion Rate
                  </p>
                  <p className="text-ink mt-1 text-2xl font-black">
                    {((performance.data?.conversionRateBps || 0) / 100).toFixed(2)}%
                  </p>
                </div>
              </div>
            ))}

          {activeTab === 'leads' &&
            (leads.isLoading ? (
              <Spinner />
            ) : (
              <div className="divide-border/60 divide-y">
                {leads.data?.map((l: any) => (
                  <div key={l.id} className="flex justify-between py-4">
                    <div>
                      <p className="text-ink text-[14px] font-bold">{l.name}</p>
                      <p className="text-ink-muted text-[12px] font-medium">
                        {l.email} • {l.phone}
                      </p>
                    </div>
                    <p className="text-ink-muted text-[12px] font-medium">
                      {new Date(l.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
                {leads.data?.length === 0 && (
                  <p className="text-ink-muted py-4 text-[13px] font-medium">No leads assigned.</p>
                )}
              </div>
            ))}

          {activeTab === 'orders' &&
            (orders.isLoading ? (
              <Spinner />
            ) : (
              <div className="divide-border/60 divide-y">
                {orders.data?.map((o: any) => (
                  <div key={o.id} className="flex justify-between py-4">
                    <div>
                      <p className="text-ink text-[14px] font-bold">{o.orderNumber}</p>
                      <p className="text-ink-muted text-[12px] font-medium">{o.customer?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-ink text-[14px] font-bold">
                        ₹{(o.grandTotalPaise / 100).toFixed(2)}
                      </p>
                      <Badge
                        tone={o.status === 'COMPLETED' ? 'success' : 'neutral'}
                        className="py-0.5 text-[10px]"
                      >
                        {o.status}
                      </Badge>
                    </div>
                  </div>
                ))}
                {orders.data?.length === 0 && (
                  <p className="text-ink-muted py-4 text-[13px] font-medium">No orders assigned.</p>
                )}
              </div>
            ))}

          {activeTab === 'payouts' &&
            (payouts.isLoading ? (
              <Spinner />
            ) : (
              <div className="divide-border/60 divide-y">
                {payouts.data?.map((p) => (
                  <div key={p.id} className="flex justify-between py-4">
                    <div>
                      <p className="text-ink text-[14px] font-bold">
                        ₹{(p.amountPaise / 100).toFixed(2)}
                      </p>
                      <p className="text-ink-muted text-[12px] font-medium">
                        ₹{(p.grossPaise / 100).toFixed(2)} commission
                        {p.tdsPaise ? ` − ₹${(p.tdsPaise / 100).toFixed(2)} TDS` : ''} ·{' '}
                        {new Date(p.createdAt).toLocaleDateString()}
                        {p.reference ? ` · UTR ${p.reference}` : ''}
                      </p>
                    </div>
                    <Badge
                      tone={
                        p.status === 'PAID'
                          ? 'success'
                          : p.status === 'PENDING'
                            ? 'warning'
                            : 'neutral'
                      }
                      className="py-0.5 text-[10px]"
                    >
                      {p.status === 'PENDING' ? 'TO PAY' : p.status}
                    </Badge>
                  </div>
                ))}
                {payouts.data?.length === 0 && (
                  <p className="text-ink-muted py-4 text-[13px] font-medium">No payouts found.</p>
                )}
              </div>
            ))}

          {activeTab === 'audit' &&
            (audit.isLoading ? (
              <Spinner />
            ) : (
              <div className="divide-border/60 divide-y">
                {audit.data?.map((a: any) => (
                  <div key={a.id} className="py-4">
                    <p className="text-ink text-[14px] font-bold">{a.action}</p>
                    <p className="text-ink-muted text-[12px] font-medium">
                      By {a.actor?.name || 'System'} on {new Date(a.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))}
                {audit.data?.length === 0 && (
                  <p className="text-ink-muted py-4 text-[13px] font-medium">
                    No audit logs found.
                  </p>
                )}
              </div>
            ))}
        </div>
      </motion.div>
    </div>
  );
}
